import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { OpenXAgentWidgetError } from '../components/OpenXAgentWidgetError.tsx';
import { answerDividerSx, columnSx } from '../utils/styles.ts';
import { useParsedWidget } from '../utils/useParsedWidget.ts';
import { OpenXAgentPersonaCard } from './components/OpenXAgentPersonaCard.tsx';
import {
  disclaimerLabelSx,
  disclaimerSx,
  personaListSx,
  sectionCaptionSx,
  sectionSx,
  sectionTitleSx,
} from './OpenXAgentPersonas.styles.ts';
import {
  getPersonaSections,
  getPersonasDisclaimer,
  hasPersonasDisclaimer,
  type PersonaSection,
} from './utils/personaSections.ts';
import { getPersonaTone, parsePersonasPayload } from './utils/personasPayload.ts';

type OpenXAgentPersonasProps = {
  payload: unknown;
};

const PersonaSectionView = ({ status, title, caption, personas }: PersonaSection) => (
  <Box component="section" sx={sectionSx} data-test={`personas-${status}`}>
    <Box>
      <Typography component="h4" sx={sectionTitleSx(getPersonaTone(status))}>
        {title}
      </Typography>
      <Typography sx={sectionCaptionSx}>{caption}</Typography>
    </Box>
    <Box component="ul" sx={personaListSx}>
      {personas.map(persona => (
        <OpenXAgentPersonaCard key={persona.id} persona={persona} />
      ))}
    </Box>
  </Box>
);

// synthetic personas that do and do not fit the deal's targeting, each section shown only when it has personas.
export const OpenXAgentPersonas = ({ payload }: OpenXAgentPersonasProps) => {
  const { t } = useTranslation();
  const parsed = useParsedWidget(payload, parsePersonasPayload, 'OpenXAgentPersonas');

  if (!parsed.ok) return <OpenXAgentWidgetError />;

  const disclaimer = getPersonasDisclaimer(t);

  return (
    <Box sx={columnSx} data-test="openx-agent-personas">
      {hasPersonasDisclaimer(parsed.value.personas) ? (
        <>
          <Divider sx={answerDividerSx} />
          <Typography sx={disclaimerSx}>
            <Box component="strong" sx={disclaimerLabelSx}>
              {disclaimer.label}
            </Box>{' '}
            {disclaimer.text}
          </Typography>
        </>
      ) : null}
      {getPersonaSections(parsed.value.personas, t).map(section => (
        <PersonaSectionView key={section.status} {...section} />
      ))}
    </Box>
  );
};
